import { useTranslation } from "react-i18next";
import { Boxes, Receipt, Store, Users } from "lucide-react";
import { PageHeader } from "@/shared/ui/layout";
import { KpiCard } from "@/features/admin/components";
import { useAuth } from "@/features/auth/auth.context";
import { formatOrderDate } from "@/features/admin/orders/format";
import { PanelBanner } from "@/features/panel/PanelBanner";
import { MemberKpis } from "@/features/panel/MemberKpis";
import { useMyDownline, useMyOrders, useMyStore } from "./minegocio.api";
import { NetworkCard, ProductsCard, SalesCard, StoreLinkCard } from "./dashboard/cards";
import { PackageAutoshipCard } from "@/features/socio/dashboard/PackageAutoshipCard";
import {
  AvailableCommissionsCard,
  CommissionClockCard,
  RankCard,
} from "@/features/socio/dashboard/SideCards";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El dashboard del franquiciatario (Anahí, 27-ago): el banner de
 * campaña arriba, sus números de un vistazo, y sus CUATRO esquinas —
 * la tienda con el enlace listo, sus productos actuales con stock, sus
 * ventas recientes y su red. Cada card es un COMPONENTE (dashboard/
 * cards.tsx) y lee el mismo endpoint que su sección: este panel
 * resume, no repite ni inventa.
 * ═══════════════════════════════════════════════════════════════════
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const store = useMyStore();
  const ventas = useMyOrders({ page: 1, perPage: 4, role: "seller" });
  const downline = useMyDownline();

  const items = store.data?.items ?? [];
  const unidades = items.reduce((sum, item) => sum + item.stock, 0);
  const enTienda = items.filter((item) => item.enabled).length;

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Dashboard")}
        description={
          user?.franchiseeSince
            ? t("Franquiciataria desde el {{fecha}}.", {
                fecha: formatOrderDate(user.franchiseeSince),
              })
            : ""
        }
      />

      {/* ── El banner de campaña, administrado en /admin/banner ──────── */}
      <PanelBanner />

      {/* ── Los números ──────────────────────────────────────────────── */}
      <div className="mb-4 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
        <KpiCard
          icon={Store}
          item="tienda"
          label={t("En tu tienda")}
          value={String(enTienda)}
          hint={t("productos a la venta")}
        />
        <KpiCard
          icon={Boxes}
          item="box"
          label={t("Inventario")}
          value={String(unidades)}
          hint={t("unidades de tus paquetes")}
        />
        <KpiCard icon={Receipt} item="tarjeta" label={t("Ventas")} value={String(ventas.data?.total ?? 0)} />
        <KpiCard
          icon={Users}
          item="mi-red"
          label={t("Mi red")}
          value={String(downline.data?.total ?? 0)}
          hint={t("hasta 8 niveles")}
        />
        {/*
          ── Lo que trae de su vida como socio (Anahí, 1-sep) ──────────
          Sus pedidos, sus puntos y sus comisiones. Estaban solo en el
          resumen del socio, así que al adquirir la franquicia y aterrizar
          aquí desaparecían de la vista: quien acababa de pagar veía MENOS
          que el día anterior. Ser franquiciatario no es dejar de ser
          socio; es serlo Y además tener tienda, y los números de la
          tienda —los cuatro de arriba— se suman a estos, no los
          sustituyen.
        */}
        <MemberKpis />
      </div>

      {/*
        ── Mis paquetes · Autoship y sus tres cards (Anahí, 28-ago): el
        mismo bloque del socio, con los paquetes que la franquicia ha
        adquirido.
      */}
      <div className="mb-4 grid items-start gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <PackageAutoshipCard basePath="/mi-negocio" variant="franchisee" />
        <div className="grid gap-4">
          <AvailableCommissionsCard basePath="/mi-negocio" />
          <RankCard basePath="/mi-negocio" />
          <CommissionClockCard />
        </div>
      </div>

      {/* ── Las cuatro esquinas, como componentes ────────────────────── */}
      <div className="grid items-start gap-4 xl:grid-cols-2">
        <div className="grid gap-4">
          <StoreLinkCard store={store.data} />
          <ProductsCard store={store.data} />
        </div>
        <div className="grid gap-4">
          <SalesCard orders={ventas.data?.items ?? []} />
          <NetworkCard
            members={downline.data?.items ?? []}
            total={downline.data?.total ?? 0}
            basePath="/mi-negocio"
          />
        </div>
      </div>
    </>
  );
}
